import React from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import {
  Box,
  Stack,
  Avatar,
  Button,
  Tooltip,
  Skeleton,
  Typography,
  IconButton,
} from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';

import { convertLocalTime } from 'src/utils/convertTime';

import Iconify from 'src/components/iconify';

import { styles } from '../styles';

ContextSidebar.propTypes = {
  ticket: PropTypes.object,
  customerData: PropTypes.object,
  agentData: PropTypes.object,
  departmentData: PropTypes.object,
  onReassign: PropTypes.func.isRequired,
  onAssign: PropTypes.func,
  onShowAnswers: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  hasWritePermission: PropTypes.bool,
  onAssignSelf: PropTypes.func,
  canViewCollab: PropTypes.bool,
};

export default function ContextSidebar({
  ticket,
  customerData,
  agentData,
  departmentData,
  onReassign,
  onAssign,
  onShowAnswers,
  onBack,
  onAssignSelf,
  hasWritePermission,
  canViewCollab,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleBack = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  const { id: currentUserId, permissions, departmentIds } = useAuth();

  const isUnassigned =
    !ticket.assignedTo ||
    Number(ticket.assignedTo) === -1 ||
    Number(ticket.assignedTo) === 0;

  const canAssign =
    permissions?.includes('TICKET_ASSIGNEES:CREATE') ||
    permissions?.includes('TICKET_ASSIGNEES:UPDATE');

  const canResolve = permissions?.includes('TICKET:RESOLVE');

  const canAssignSelf =
    isUnassigned &&
    canAssign &&
    canResolve &&
    departmentIds?.includes(ticket.departmentId) &&
    ticket.createdBy !== currentUserId;

  const formatDate = (date) =>
    date ? format(convertLocalTime(new Date(date)), 'dd/MM/yyyy HH:mm') : '—';

  // --- CSV GENERATION LOGIC (Sanitized against Formula Injection) ---
  const generateCSV = () => {
    if (!ticket || !ticket.data) return '';

    const sanitizeCsvField = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value).trim();
      if (
        str.startsWith('=') ||
        str.startsWith('+') ||
        str.startsWith('-') ||
        str.startsWith('@')
      ) {
        return `'${str}`; // Prepends a single quote to force text interpretation
      }
      return str;
    };

    let csvContent = `${t('pages.ticketDetails.question')},${t(
      'pages.ticketDetails.answer'
    )}\n`;
    ticket.data.forEach((section) => {
      section.questions?.forEach((q) => {
        const cleanQuestion = (q.text || '').replace(/"/g, '""');
        const questionText = `"${sanitizeCsvField(cleanQuestion)}"`;

        const rawAnswer = Array.isArray(q.inputValue)
          ? q.inputValue.join(', ')
          : String(q.inputValue || '');
        const cleanAnswer = rawAnswer.replace(/"/g, '""');
        const answerText = `"${sanitizeCsvField(cleanAnswer)}"`;

        csvContent += `${questionText},${answerText}\n`;
      });
    });
    return csvContent;
  };

  const dataInCSV = generateCSV();

  if (!ticket) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
        <Skeleton
          variant="rectangular"
          height={200}
          sx={{ borderRadius: '16px', mb: 3 }}
        />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '16px' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', position: 'relative', p: 2 }}>
      <Button
        onClick={handleBack}
        disableRipple
        sx={{
          position: 'relative',
          justifyContent: 'flex-start',
          color: 'text.secondary',
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.92rem',
          letterSpacing: '0.02em',
          pl: 2.5,
          pr: 2,
          py: 1.1,
          mb: 2.5,
          gap: 1.25,
          borderRadius: '4px',
          minWidth: 0,
          transition: 'color 0.2s ease, background-color 0.2s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            width: 4,
            height: 20,
            marginTop: '-10px',
            borderRadius: '2px',
            bgcolor: 'rgba(145, 158, 171, 0.55)',
            transition:
              'height 0.25s cubic-bezier(0.4, 0, 0.2, 1), margin-top 0.25s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.25s ease',
          },
          '&:hover': {
            bgcolor: 'action.hover',
            color: 'text.primary',
            '& .back-icon': { transform: 'translateX(-4px)' },
          },
        }}
      >
        <Iconify
          className="back-icon"
          icon="solar:round-arrow-left-line-duotone"
          width={24}
          sx={{ transition: 'transform 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
        />
        {t('ticket.back')}
      </Button>

      <Stack
        spacing={3}
        sx={{ height: 'calc(100% - 60px)', overflowY: 'auto', ...styles.scrollbarCustom }}
      >
        {/* SECTION 1: TICKET INFO */}
        <Box sx={{ ...styles.cardBase, p: 2.5, backgroundColor: 'background.neutral' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2.5,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.2}>
              <Box
                component="img"
                src="/assets/icons/Tickets/ticket.svg"
                sx={{
                  width: 27,
                  height: 27,
                  filter:
                    theme.palette.mode === 'dark' ? 'brightness(0) invert(0.9)' : 'none',
                  transition: 'filter 0.25s',
                }}
              />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  letterSpacing: '0.05em',
                  color: 'text.primary',
                }}
              >
                {t('ticket.Ticket Info')}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Tooltip title={t('buttons.downloadAnswers')} placement="top">
                <IconButton
                  component="a"
                  href={`data:text/csv;charset=utf-8,${encodeURIComponent(dataInCSV)}`}
                  download={`ticket-${ticket.id || 'export'}-Answers.csv`}
                  size="small"
                  sx={{
                    color: '#00A76F',
                    bgcolor: 'rgba(0, 167, 111, 0.08)',
                    borderRadius: '8px',
                    '&:hover': { bgcolor: 'rgba(0, 167, 111, 0.16)' },
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/icons/Tickets/Import.svg"
                    sx={{
                      width: 24,
                      height: 24,
                      filter:
                        'invert(42%) sepia(93%) saturate(1352%) hue-rotate(130deg) brightness(95%) contrast(101%)',
                    }}
                  />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('pages.ticketDetails.formResponses')} placement="top">
                <IconButton
                  onClick={onShowAnswers}
                  size="small"
                  sx={{
                    color: '#00A76F',
                    bgcolor: 'rgba(0, 167, 111, 0.08)',
                    borderRadius: '8px',
                    '&:hover': { bgcolor: 'rgba(0, 167, 111, 0.16)' },
                  }}
                >
                  <Iconify icon="solar:menu-dots-bold-duotone" width={24} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                sx={{ color: 'text.primary', fontSize: '0.85rem' }}
              >
                {t('ticket.postedBy')}:
              </Typography>
              <Avatar
                src={customerData?.avatarUrl}
                sx={{ width: 30, height: 30 }}
                imgProps={{ loading: 'lazy' }}
              />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {customerData?.username || '—'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Typography
                variant="body2"
                sx={{ color: 'text.primary', fontSize: '0.85rem' }}
              >
                {t('ticket.createdAt')}:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {formatDate(ticket.createdAt)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Typography
                variant="body2"
                sx={{ color: 'text.primary', fontSize: '0.85rem' }}
              >
                {t('ticket.lastUpdate')}:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {formatDate(ticket.updatedAt)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                sx={{ color: 'text.primary', fontSize: '0.85rem' }}
              >
                {t('ticket.department')}:
              </Typography>
              <Iconify icon="solar:home-2-bold" width={16} color="text.secondary" />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {departmentData?.name || '—'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                sx={{ color: 'text.primary', fontSize: '0.85rem' }}
              >
                {t('ticket.estimatedDuration')}:
              </Typography>
              <Iconify icon="solar:clock-circle-bold" width={16} color="text.secondary" />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {ticket.estimatedDuration?.replace('PT', '').replace('H', '') || '1'}{' '}
                {t('ticket.hours')}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* SECTION 2: AGENT INFO */}
        {canViewCollab && (
          <Box sx={{ ...styles.cardBase, p: 2.5, backgroundColor: 'background.neutral' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2.5,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.2}>
                <Box
                  component="img"
                  src="/assets/icons/Tickets/agent.svg"
                  sx={{
                    width: 27,
                    height: 27,
                    filter:
                      theme.palette.mode === 'dark'
                        ? 'brightness(0) invert(0.9)'
                        : 'none',
                    transition: 'filter 0.25s',
                  }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '1rem',
                    letterSpacing: '0.05em',
                    color: 'text.primary',
                  }}
                >
                  {t('ticket.Agent Info')}
                </Typography>
              </Stack>

              {canAssign && hasWritePermission && (
                <Stack direction="row" spacing={1}>
                  {/* . Assign to Self Button */}
                  {canAssignSelf && (
                    <Tooltip
                      title={t('pages.assignTicket.assignTicketToSelf')}
                      placement="top"
                    >
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          if (onAssignSelf) onAssignSelf();
                        }}
                        size="small"
                        sx={{
                          color: '#00A76F',
                          bgcolor: 'rgba(0, 167, 111, 0.08)',
                          borderRadius: '8px',
                          '&:hover': { bgcolor: 'rgba(0, 167, 111, 0.16)' },
                        }}
                      >
                        <Iconify icon="solar:user-id-bold-duotone" width={24} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {/* . Assign / Reassign Button */}
                  <Tooltip
                    title={
                      isUnassigned
                        ? t('pages.assignTicket.assignTicketTo')
                        : t('ticket.Reassign Ticket')
                    }
                    placement="top"
                  >
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        if (isUnassigned && onAssign) {
                          onAssign();
                        } else {
                          onReassign();
                        }
                      }}
                      size="small"
                      sx={{
                        color: '#00A76F',
                        bgcolor: 'rgba(0, 167, 111, 0.08)',
                        borderRadius: '8px',
                        '&:hover': { bgcolor: 'rgba(0, 167, 111, 0.16)' },
                      }}
                    >
                      <Iconify
                        icon={
                          isUnassigned
                            ? 'eva:person-add-fill'
                            : 'solar:transfer-horizontal-bold-duotone'
                        }
                        width={24}
                      />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Box>

            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar
                  src={agentData?.avatarUrl}
                  sx={{ width: 30, height: 30 }}
                  imgProps={{ loading: 'lazy' }}
                />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {agentData?.username || t('status.UNASSIGNED')}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.primary', fontSize: '0.85rem' }}
                >
                  {t('ticket.email')}:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {agentData?.email || '—'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.primary', fontSize: '0.85rem' }}
                >
                  {t('ticket.noPhone')}:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {agentData?.phoneNumber || '—'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="body2"
                  sx={{ color: 'text.primary', fontSize: '0.85rem' }}
                >
                  {t('ticket.Department')}:
                </Typography>
                <Iconify icon="solar:home-2-bold" width={16} color="text.secondary" />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {departmentData?.name || '—'}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
